import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { CreditCardIcon, DocumentTextIcon, ChartBarIcon, InformationCircleIcon } from '@heroicons/react/24/solid';
import { generateReport, validateToken } from '../services/apiLabzService';
import { getBoardData } from '../services/trelloService';
import ProgressDialog from './ProgressDialog';

const ReportGenerator = ({ token, credits, setCredits }) => {
    const [instructions, setInstructions] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [reportUrl, setReportUrl] = useState('');
    const [reportType, setReportType] = useState(null);
    const [error, setError] = useState(null);

    const handleGenerateReport = async (type) => {
        setIsGenerating(true);
        setReportUrl('');
        setReportType(type);
        setError(null);

        try {
            const t = window.TrelloPowerUp.iframe();
            const boardData = await getBoardData(t);
            
            // Set a timeout of 3 minutes (180000 ms)
            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Request timed out')), 180000)
            );
            
            const reportPromise = generateReport(token, type, boardData, instructions);
            
            const report = await Promise.race([reportPromise, timeoutPromise]);
            
            if (type === 'text') {
                const blob = new Blob([report], { type: 'text/html' });
                setReportUrl(URL.createObjectURL(blob));
            } else {
                setReportUrl(report.fileURL);
            }

            const updatedCredits = await validateToken(token);
            if (updatedCredits.isValid) {
                setCredits(updatedCredits.credits);
            } else {
                throw new Error('Token became invalid');
            }
            
            toast.success('Report generated successfully!');
        } catch (error) {
            console.error('Error generating report:', error);
            setError('Unable to load report. Please check your dashboard for status.');
            setReportType(null);
        }
        setIsGenerating(false);
    };

    const handleDownload = () => {
        window.open(reportUrl, '_blank');
    };

    const handleCheckDashboard = () => {
        window.open('https://apilabz.com/api-labs/dashboard', '_blank');
    };

    return (
        <div className="bg-white p-4">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                    <InformationCircleIcon className="h-5 w-5 text-blue-500 mr-2" />
                    <span className="text-sm font-semibold">Instructions</span>
                </div>
                <div className="flex items-center">
                    <CreditCardIcon className="h-5 w-5 text-green-500 mr-2" />
                    <span className="text-sm font-semibold">Credits: {credits}</span>
                </div>
            </div>
            <div className="mb-4">
                <textarea
                    rows="3"
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Enter your instructions here"
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                ></textarea>
            </div>
            <div className="space-y-2">
                {isGenerating ? (
                    <ProgressDialog />
                ) : error ? (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
                        <p className="text-red-700 mb-2">{error}</p>
                        <button
                            onClick={handleCheckDashboard}
                            className="w-full bg-blue-500 text-white px-4 py-2 rounded-md flex items-center justify-center text-sm"
                        >
                            Check Status on Dashboard
                        </button>
                    </div>
                ) : (
                    <>
                        <button
                            onClick={() => handleGenerateReport('graphic')}
                            className="w-full bg-green-500 text-white px-4 py-2 rounded-md flex items-center justify-center text-sm"
                        >
                            <ChartBarIcon className="h-4 w-4 mr-2" />
                            Generate AI Graphic Report
                        </button>
                    </>
                )}
            </div>
            {reportUrl && !isGenerating && (
                <div className="mt-4 bg-gray-100 p-3 rounded-md">
                    <button
                        onClick={handleDownload}
                        className="w-full bg-blue-500 text-white px-4 py-2 rounded-md text-sm"
                    >
                        Download Report
                    </button>
                </div>
            )}
            
            {/* Footer with links */}
            <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center text-xs">
                    <a href="mailto:info@apilabz.com" className="text-blue-500 hover:text-blue-700">Email Us</a>
                    <a href="https://apilabz.com" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">Website</a>
                    <a href="https://trello.com/power-ups/67de6ae42f31217407680abf" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">AI Apps</a>
                    <a href="https://apilabz.com/api-labs/dashboard" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-700">Activity</a>
                </div>
            </div>
        </div>
    );
};

export default ReportGenerator;